import {
	OnyxDataFilter as OnyxDataFilterType,
	OnyxDataFilterOptions,
	ONYX_FILTER_CATEGORIES,
} from '@/types';
import React, { useMemo } from 'react';
import { MultiSelectDropdown } from '../ui/MultiSelectDropdown';
import { applyCascadingFilters, pruneOrphanedSelections } from '@/utils/cascadingFilters';

interface OnyxDataFilterProps {
	onyxFilters: OnyxDataFilterType
	onChange: (newFilters: OnyxDataFilterType) => void;
	filterOptions: OnyxDataFilterOptions
}

export const OnyxDataFilter: React.FC<OnyxDataFilterProps> = ({
	onyxFilters,
	onChange,
	filterOptions
}) => {

	// ONYX has no Site filter, so only the Major Component → Sub-Component
	// cascade applies here.
	const displayedOptions = useMemo(
		() =>
			applyCascadingFilters(filterOptions, {
				componentsMajor: onyxFilters.componentsMajor,
			}),
		[filterOptions, onyxFilters.componentsMajor],
	);

	const handleChange = (key: keyof OnyxDataFilterType, value: string[]) => {
		let next: OnyxDataFilterType = { ...onyxFilters, [key]: value };
		if (key === 'componentsMajor') {
			const recomputed = applyCascadingFilters(filterOptions, {
				componentsMajor: next.componentsMajor,
			});
			next = pruneOrphanedSelections(next, {
				turbineModels: recomputed.turbineModels,
				componentsMinor: recomputed.componentsMinor,
			});
		}
		onChange(next);
	};

	return (
		<div className="space-y-4">
			{Object.entries(ONYX_FILTER_CATEGORIES).map(([key, config]) => {
				const rawOptions = displayedOptions[key as keyof typeof displayedOptions];
				const options: string[] = Array.isArray(rawOptions) ? rawOptions : [];
				const currentValues = onyxFilters[config.key] ?? [];
				return (
					<div key={key} className='relative'>
						<label className='block text-sm font-medium text-gray-700 mb-2'>
							{config.label}
							<span className='text-grey-400 text-xs ml-1'>{options.length}</span>
						</label>
						<MultiSelectDropdown
							options={options.map(option => ({ value: option, label: option }))}
							selectedValues={currentValues}
							onChange={(value) => handleChange(config.key as keyof OnyxDataFilterType, value)}
							placeholder={config.placeholder}
							className='w-full'
						/>
					</div>
				)
			})}
		</div>);
};

export default OnyxDataFilter;